import { useCallback, useEffect, useRef, useState } from 'react';
import { APP_VERSION } from 'virtual:app-version';
import { accountLevel, canReadMetrics, type Intake } from '@lenderhound/shared';
import {
  me, logout, search, ApiError,
  type Answer, type Coverage, type LenderGroup, type Reference, type SectionTitle,
  type User,
} from './api.ts';
import Login from './components/Login.tsx';
import VerifyGate from './components/VerifyGate.tsx';
import ScenarioDesk from './components/ScenarioDesk.tsx';
import UpdatingBanner from './components/UpdatingBanner.tsx';
import Results from './components/Results.tsx';
import Chat from './components/Chat.tsx';
import Admin from './components/Admin.tsx';
import Metrics from './components/Metrics.tsx';
import ContactUs from './components/ContactUs.tsx';
import type { Stage } from './components/SearchProgress.tsx';
import { recordStep, recordVisit } from './journey.ts';

/**
 * Which of the three screens the header is pointing at.
 *
 * This was a `showAdmin` boolean. A third screen turns that into two booleans that can both
 * be true, and one state that says which is the only shape where that cannot happen.
 */
type View = 'search' | 'admin' | 'metrics' | 'contact';

/**
 * Every screen has a URL, and that is what makes the browser's Back button work.
 *
 * Henry Liner, 2026-09-17, having pressed Contact us: "Can't press back ... I have to press
 * back to search at the bottom, not good UI ... It's like a weird toggle". He was right on
 * both counts: switching screens changed a variable and left no history entry, so Back left
 * the site entirely, and the only way home was a link at the foot of the page.
 *
 * A hand-written four-line router rather than a routing library: there are four screens, they
 * take no parameters, and Caddy already serves index.html for unknown paths (infra/Caddyfile,
 * `try_files {path} /index.html`), so /contact is a real link somebody can send.
 */
const PATHS: Record<View, string> = {
  search: '/', contact: '/contact', admin: '/admin', metrics: '/metrics',
};

function viewFromPath(path: string): View {
  for (const [view, p] of Object.entries(PATHS)) if (p === path && p !== '/') return view as View;
  return 'search';
}

/**
 * A stage carries the moment it finished rather than a boolean, so the progress card can
 * report how long each one actually took. `done: boolean` threw that away: the three
 * stages span two seconds, three seconds and a minute and a half, and collapsing them to
 * ticked or not ticked is what made the third one indistinguishable from a hang.
 */
const INITIAL_STAGES: Stage[] = [
  { label: 'Checking every lender matrix', doneAt: null },
  { label: 'Reading the guidelines', doneAt: null },
  { label: 'Writing the cited answer', doneAt: null },
];

/**
 * The same three stages when there is no free text, where the middle one does not happen.
 *
 * The pipeline still emits the sections frame - carrying an empty list - so the progress
 * card would tick "Reading the guidelines" in about a second and claim work that was
 * never done. Naming the stage for what it actually is keeps the card honest, and it is
 * also the first place the broker sees that leaving the box empty changed the search.
 */
const MATRIX_ONLY_STAGES: Stage[] = [
  { label: 'Checking every lender matrix', doneAt: null },
  { label: 'Skipping the guidelines - nothing was flagged as unusual', doneAt: null },
  { label: 'Writing the cited answer', doneAt: null },
];

/**
 * What to show a broker when a request fails.
 *
 * ApiError carries the wire code as its message, and that message was rendered straight
 * into the red banner - so a session expiring mid-search printed the literal word
 * "unauthenticated", and a rejected intake printed "invalid_intake". Anything unmapped
 * still falls through to the raw text rather than to a shrug, because an unrecognised
 * failure with a strange word in it is more useful than a generic one.
 */
function friendlyError(err: unknown): string {
  // A server-written sentence wins over anything mapped here. The daily search ceiling
  // is the case that needs it: the reset time and the size of the allowance are only
  // known server-side, and a broker who has run out needs both to decide whether to wait.
  if (err instanceof ApiError && err.detail) return err.detail;
  const code = err instanceof Error ? err.message : '';
  switch (code) {
    case 'unauthenticated':
      return 'Your session has expired. Sign in again and the file is still here.';
    case 'forbidden':
      return 'This account does not have access to that.';
    case 'invalid_intake':
      return 'Something in the file did not validate. Check the form and try again.';
    case 'rate_limited':
      return 'Too many requests just now. Wait a moment and try again.';
    case 'search_failed':
    case 'request_failed':
      return 'The search could not be completed. Try again.';
    default:
      return code || 'The search could not be completed. Try again.';
  }
}

export default function App() {
  const [user, setUser] = useState<User | null>(null);
  const [ready, setReady] = useState(false);
  const [busy, setBusy] = useState(false);
  const [stages, setStages] = useState<Stage[]>(INITIAL_STAGES);
  // performance.now() at submit, and the section list the retrieval step returned. Both
  // exist only so the wait can be described: the clock needs an origin, and the titles
  // are what the third stage is actually reading.
  const [startedAt, setStartedAt] = useState<number | null>(null);
  const [sections, setSections] = useState<SectionTitle[] | null>(null);
  const [failed, setFailed] = useState(false);
  const [lenders, setLenders] = useState<LenderGroup[] | null>(null);
  const [coverage, setCoverage] = useState<Coverage | null>(null);
  const [answer, setAnswer] = useState<Answer | null>(null);
  const [expertReview, setExpertReview] = useState<string | null>(null);
  // What the answer was built out of, so Results can say so before anyone reads the prose.
  const [basis, setBasis] = useState<'matrix_only' | 'matrix_and_guidelines' | null>(null);
  const [dropped, setDropped] = useState(0);
  // Lenders the search could not finish checking because something on our side failed.
  // Held apart from every other piece of state on this page for the reason it is held
  // apart on the wire: it is the one caveat that is about us rather than about the loan.
  const [systemFailures, setSystemFailures] =
    useState<{ lenderSlug: string; lenderName: string }[]>([]);
  const [references, setReferences] = useState<Map<number, Reference>>(new Map());
  // Whether the `references` frame has landed. It arrives one frame after `answer`, so
  // without this a chip clicked in that gap is indistinguishable from a chip whose
  // source genuinely could not be resolved. See Results.
  const [refsArrived, setRefsArrived] = useState(false);
  const [error, setError] = useState('');
  const [inquiryId, setInquiryId] = useState<string | null>(null);
  const [openRef, setOpenRef] = useState<number | null>(null);
  // Read from the address bar, so a reload or a shared link opens the screen it names.
  const [view, setView] = useState<View>(() => viewFromPath(window.location.pathname));
  // A file submitted before anyone knew who was asking. Held here rather than in the gate so
  // that exactly this intake is what runs once the code is accepted - the broker is never
  // asked to fill the form in twice. See VerifyGate.
  const [pendingIntake, setPendingIntake] = useState<Intake | null>(null);
  // The password form, reached from "Staff sign in". It is no longer the front door.
  const [staffSignIn, setStaffSignIn] = useState(false);
  const abortRef = useRef<AbortController | null>(null);

  // Back and Forward. `popstate` fires only for entries this app pushed, so the browser's own
  // history - the page somebody was on before LenderHound - still works from the search screen.
  useEffect(() => {
    const onPop = () => setView(viewFromPath(window.location.pathname));
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  useEffect(() => {
    me().then((r) => setUser(r.user)).catch(() => setUser(null)).finally(() => setReady(true));
    // The top of Henry's funnel - "100 people visited but only 5 queries". Once per load;
    // journey.ts holds the guard, so StrictMode's second mount effect records nothing.
    recordVisit();
  }, []);

  // A search in flight when the component goes away must be cancelled, or the server
  // keeps a ninety-second model call running for a page nobody is looking at.
  useEffect(() => () => abortRef.current?.abort(), []);

  // Stamped once per call, not per stage: a stage that is being marked done because a
  // later one arrived first must not be given a completion time later than the stage
  // that overtook it, or the progress card renders a negative duration.
  const markStage = useCallback((i: number) => {
    const at = performance.now();
    setStages((prev) => prev.map((s, j) => (j <= i && s.doneAt === null ? { ...s, doneAt: at } : s)));
  }, []);

  const onSubmit = useCallback(async (intake: Intake) => {
    abortRef.current?.abort();
    const ctl = new AbortController();
    abortRef.current = ctl;

    setBusy(true); setError(''); setFailed(false);
    // Chosen from the same condition the server switches on - an empty funky box means
    // the guideline half does not run. Duplicated here rather than waited for, because
    // the stage list is on screen before the first frame comes back.
    setStages((intake.funkyText.trim() ? INITIAL_STAGES : MATRIX_ONLY_STAGES)
      .map((s) => ({ ...s })));
    setStartedAt(performance.now());
    setSections(null);
    setLenders(null); setCoverage(null); setAnswer(null); setExpertReview(null);
    setBasis(null);
    setDropped(0);
    setSystemFailures([]);
    setReferences(new Map()); setRefsArrived(false);
    setInquiryId(null); setOpenRef(null);

    try {
      await search(intake, (e) => {
        switch (e.type) {
          case 'inquiry':
            setInquiryId(e.inquiryId); break;
          case 'verdicts':
            setLenders(e.lenders); setCoverage(e.coverage); markStage(0); break;
          case 'sections':
            // The titles were being thrown away. They are the only concrete thing the
            // broker can be shown during the ninety seconds that follow: which lenders'
            // guidelines, and which sections of them, are in front of the model.
            setSections(e.titles); markStage(1); break;
          case 'answer':
            // The bottom of the funnel: an answer on screen, not merely a search started.
            void recordStep('answer');
            setAnswer(e.answer);
            setBasis(e.basis ?? null);
            setExpertReview(e.expertReview?.offered ? e.expertReview.message : null);
            setDropped(e.droppedFindings ?? 0);
            setSystemFailures(e.systemFailures ?? []);
            markStage(2); break;
          case 'references':
            setReferences(new Map(e.references.map((r) => [Number(r.id), r])));
            // Set even on the server's degraded path, where this frame carries an empty
            // set: it means the lookup is over, not that it has not happened.
            setRefsArrived(true);
            break;
          case 'error':
            // Stop the spinners too. Handling this by setting the message alone left
            // stages 2 and 3 visibly spinning underneath a red error card, so a failed
            // run looked like a failed run that was still working.
            //
            // It stops them with a `failed` flag rather than by marking every stage
            // done, which is what it used to do: that put three green ticks under a red
            // banner, so a search that never reached synthesis claimed to have completed
            // all three steps. The unreached stages now go grey and un-ticked.
            setError(e.message);
            setFailed(true);
            break;
          default:
            break;
        }
      }, ctl.signal);
    } catch (err) {
      if (ctl.signal.aborted) {
        // Cancelled on purpose - a newer search, a sign-out, the page going away.
      } else if (err instanceof ApiError && err.code === 'unauthenticated') {
        // The session ended between loading the page and pressing Search - the thirty days ran
        // out, or it was signed out elsewhere. That is the gate's job rather than a red
        // banner: the file goes back into the hand and the broker is asked who they are.
        setUser(null);
        setPendingIntake(intake);
      } else {
        setError(friendlyError(err));
        setFailed(true);
      }
    } finally {
      setBusy(false);
    }
  }, [markStage]);

  const onVerified = useCallback((u: User) => {
    setUser(u);
    const intake = pendingIntake;
    setPendingIntake(null);
    if (intake) void onSubmit(intake);
  }, [pendingIntake, onSubmit]);

  /**
   * Sign out, and take the last answer off the screen with the session.
   *
   * It used to be enough to clear the user, because that swapped the whole page for the
   * sign-in form. The desk is public now and stays up, so the previous account's lenders,
   * citations and follow-up thread would otherwise sit there for whoever uses the browser
   * next - with a chat box that 401s.
   */
  /** Go to a screen and leave a history entry, so Back returns to the one before it. */
  const go = useCallback((next: View) => {
    setView(next);
    if (window.location.pathname !== PATHS[next]) {
      window.history.pushState({ view: next }, '', PATHS[next]);
    }
  }, []);

  const signOut = useCallback(() => {
    void logout().then(() => {
      abortRef.current?.abort();
      setUser(null); go('search');
      setLenders(null); setCoverage(null); setAnswer(null); setExpertReview(null);
      setBasis(null); setSystemFailures([]); setSections(null);
      setReferences(new Map()); setRefsArrived(false);
      setInquiryId(null); setOpenRef(null); setError(''); setFailed(false);
    });
  }, []);

  // Which screen is actually drawn. A view the account may not open falls back to search,
  // exactly as the server's own guards would refuse it. Computed - and reconciled - ABOVE the
  // early returns below, because a hook that runs only sometimes is not a hook.
  const showing: View = view === 'contact' ? 'contact'
    : view === 'admin' && user?.isAdmin ? 'admin'
    : view === 'metrics' && user && canReadMetrics(user) ? 'metrics'
    : 'search';
  // A screen this account may not open - /admin typed in by a broker, or a staff screen left
  // open through a sign-out - falls back to search above. Put the address bar back in step
  // with it, replacing rather than pushing so Back does not land on the refused screen again.
  useEffect(() => {
    if (showing === view) return;
    setView(showing);
    window.history.replaceState({ view: showing }, '', PATHS[showing]);
  }, [showing, view]);

  if (!ready) return null;
  // Staff only, by an explicit click. A broker never needs this screen: Search asks them
  // for a phone instead. The admin console and the source pages stay behind the same
  // server-side guards as before; this only moves where the password form lives.
  if (staffSignIn && !user) {
    return (
      <Login onSignedIn={(u) => {
               setUser(u); setStaffSignIn(false); setPendingIntake(null);
               // A step carrying the new session, so this browser is marked as ours and every
               // anonymous visit it made before is dropped from the traffic report. Without
               // it, a browser that signs in only to read the report stays a "visitor".
               void recordStep('visit');
             }}
             onCancel={() => setStaffSignIn(false)} />
    );
  }

  const gateOpen = !user && pendingIntake !== null;
  const started = busy || lenders !== null || answer !== null;
  // Once the stream is over nothing further can arrive, whether the references frame
  // came or not - so a wait that outlives the request is a failure, and saying so beats
  // a spinner that never resolves.
  const referencesReady = refsArrived || !busy;

  return (
    <div style={{ minHeight: '100%', paddingBottom: 40 }}>
      {/* Wraps so that on a phone the account controls drop to a second row. Without it
          the row did not fit at 400px even before the logo was added: the header was
          489px wide inside 385, and Sign out was pushed off the right edge of the screen. */}
      <header style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        flexWrap: 'wrap', rowGap: 10, columnGap: 16,
        padding: '14px 24px', borderBottom: '1px solid var(--cream-line)',
        background: 'var(--paper)',
      }}>
        {/* Two rows of alignment. The name and the build number share a text baseline;
            an image has no baseline to share - in a baseline row its bottom edge would sit
            on the text's baseline and the icon would ride high - so it is centred against
            that row from outside it instead. */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {/* The logo and the name are the way home, which is the search desk - Henry:
              "pressing logo should be home. And home is search". A button rather than an
              anchor so it never reloads the page, styled back to plain text. */}
          <button type="button" onClick={() => go('search')}
                  aria-label="LenderHound home"
                  style={{
                    display: 'flex', alignItems: 'center', gap: 10, background: 'none',
                    border: 0, padding: 0, cursor: 'pointer', font: 'inherit', color: 'inherit',
                  }}>
            {/* alt="" because the name is right beside it: a screen reader would otherwise
                announce "LenderHound" twice. Width and height are given so the header does
                not shift when the image arrives. The file is 96px, sharp on 2x screens. */}
            <img src="/logo.png" alt="" width={34} height={34}
                 style={{ display: 'block', flexShrink: 0 }} />
            <span className="serif" style={{ fontSize: 22 }}>LenderHound</span>
          </button>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
            {/* Small, muted, and selectable: it is for reading out and pasting into a bug
                report, not for looking at. A trailing "+" means the running build has
                uncommitted changes and is therefore NOT the commit it names. */}
            <span
              title={`Build ${APP_VERSION}${APP_VERSION.endsWith('+')
                ? ' - includes uncommitted changes' : ''}`}
              style={{
                fontSize: 11, color: 'var(--faint)', fontVariantNumeric: 'tabular-nums',
                letterSpacing: '0.02em',
              }}
            >
              {APP_VERSION}
            </span>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          {user ? (
            <>
              {/* Shown only to admins, and the server checks again on every request - this
                  is a convenience, not the access control. */}
              {/* One label, always. These used to read "Search" while you were on the screen
                  they open, which Henry called "a weird toggle" - the way back is Back, or
                  the logo. `aria-current` marks the screen you are on. */}
              {user.isAdmin ? (
                <button className={`btn ${showing === 'admin' ? '' : 'btn-ghost'}`}
                        aria-current={showing === 'admin' ? 'page' : undefined}
                        onClick={() => go('admin')}>
                  Admin
                </button>
              ) : null}
              {/* Admin OR superuser, which is a wider door than the one beside it and
                  deliberately so: Henry is is_admin = false, is_superuser = true, and the
                  report is his request. The same predicate guards the route server-side -
                  see canReadMetrics in @lenderhound/shared - so this button cannot come to
                  disagree with what /api/admin/metrics actually serves. */}
              {canReadMetrics(user) ? (
                <button className={`btn ${showing === 'metrics' ? '' : 'btn-ghost'}`}
                        aria-current={showing === 'metrics' ? 'page' : undefined}
                        onClick={() => go('metrics')}>
                  Metrics
                </button>
              ) : null}
              {/* Brokers only. Staff are who the messages go to, and a staff header already
                  holds Admin and Metrics: with this beside them the admin row ran 115px past a
                  390px phone and pushed Sign out off the screen. */}
              {canReadMetrics(user) ? null : (
                <button className={`btn ${showing === 'contact' ? '' : 'btn-ghost'}`}
                        aria-current={showing === 'contact' ? 'page' : undefined}
                        onClick={() => go('contact')}>
                  Contact us
                </button>
              )}
              {/* A phone broker has no username; the name they gave is what shows. */}
              <span style={{ fontSize: 13, color: 'var(--muted)' }}>
                {user.displayName ?? user.username ?? 'Signed in'}
              </span>
              <button className="btn btn-ghost" onClick={signOut}>Sign out</button>
            </>
          ) : (
            <>
              {/* Public: somebody deciding whether to sign up is exactly who writes in. */}
              <button className={`btn ${showing === 'contact' ? '' : 'btn-ghost'}`}
                      aria-current={showing === 'contact' ? 'page' : undefined}
                      onClick={() => go('contact')}>
                Contact us
              </button>
              <button className="btn btn-ghost" onClick={() => setStaffSignIn(true)}>
                Staff sign in
              </button>
            </>
          )}
        </div>
      </header>

      <main style={{ padding: '28px 20px' }}>
        {showing === 'contact' ? <ContactUs onClose={() => go('search')} /> : null}
        {showing === 'admin' ? <Admin onClose={() => go('search')} /> : null}
        {showing === 'metrics' ? <Metrics onClose={() => go('search')} /> : null}
        {/*
          The search tree stays MOUNTED under every other screen, hidden rather than replaced.
          A broker's draft lives only in ScenarioDesk's own state, the phone gate's in
          VerifyGate's, and the follow-up thread in Chat's - so swapping this tree out for the
          Contact us page, which invites exactly that trip ("Questions about a file..."),
          came back to an empty form, a reset gate that would text them a second code, and a
          vanished conversation. Same trap, same answer, as the gate's hidden desk below.
        */}
        <div hidden={showing !== 'search'}>
        {/*
          ABOVE the form, not below it, and above the results too. It exists to be read
          BEFORE a broker spends a dollar on an answer that may be missing a lender - a
          warning under the results is a post-mortem. See UpdatingBanner.tsx.
        */}
        <UpdatingBanner />
        {gateOpen ? (
          <VerifyGate onVerified={onVerified} onCancel={() => setPendingIntake(null)} />
        ) : null}
        {/* Hidden rather than unmounted while the gate is up, so "Back to the file" returns
            to every answer the broker typed - the draft lives in ScenarioDesk's own state. */}
        <div hidden={gateOpen}>
          <ScenarioDesk
            onSubmit={(i) => {
              // Recorded at the press, signed in or not, and only here: the held search that
              // runs after the gate is the same press, not a second one.
              void recordStep('search');
              if (user) void onSubmit(i); else setPendingIntake(i);
            }}
            onStarted={() => void recordStep('form_start')}
            onBlocked={(fields) => void recordStep('search_blocked', fields)}
            busy={busy} />
        </div>
        {error ? (
          <div className="card" style={{
            maxWidth: 940, margin: '18px auto 0', borderColor: 'var(--red)',
            color: 'var(--red)',
          }}>{error}</div>
        ) : null}
        {/* `inquiryId` is passed for the AE-contact click metric (migration 054), not for
            anything the page renders. It arrives on the first frame of the stream, so it
            is set long before a card exists to click; a click that somehow beat it is
            still recorded, just without the search it came from. */}
        {started ? (
          <Results level={user ? accountLevel(user) : 'broker'} lenders={lenders} coverage={coverage}
                   answer={answer}
                   expertReview={expertReview} droppedFindings={dropped} basis={basis}
                   systemFailures={systemFailures}
                   references={references} referencesReady={referencesReady}
                   stages={stages}
                   startedAt={startedAt} sections={sections} failed={failed}
                   openCitationId={openRef} onOpenHandled={() => setOpenRef(null)}
                   inquiryId={inquiryId} />
        ) : null}
        {/* Chat opens once the cited answer exists. Offering it while the grid is still
            rendering invites a follow-up about an answer the broker has not read. */}
        {user && inquiryId && answer ? (
          <div style={{ maxWidth: 940, margin: '0 auto' }}>
            <Chat
              inquiryId={inquiryId}
              onReferences={(refs) => setReferences((prev) => {
                // Merge, never replace: a chip from the opening answer has to keep
                // working after a follow-up brings in its own references.
                const next = new Map(prev);
                for (const r of refs) next.set(Number(r.id), r);
                return next;
              })}
              onCite={setOpenRef}
            />
          </div>
        ) : null}
        </div>
      </main>
    </div>
  );
}
